import { ZButton } from "@/shared/components/button";
import { Modal, ModalBody, ModalContent, Input } from "@nextui-org/react";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { ControlSet } from "../../requests";

interface RenameControlSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRename: (newName: string) => void;
  controlSet: ControlSet | null;
  isRenaming?: boolean;
  errorMessage?: string;
}

const RenameControlSetModal = ({
  isOpen,
  onClose,
  onRename,
  controlSet,
  isRenaming = false,
  errorMessage,
}: RenameControlSetModalProps) => {
  const [controlSetName, setControlSetName] = useState("");

  useEffect(() => {
    if (isOpen && controlSet) {
      setControlSetName(controlSet.controls_set_name);
    }
  }, [isOpen, controlSet]);

  const handleClose = () => {
    setControlSetName("");
    onClose();
  };

  const handleSubmit = () => {
    if (controlSetName.trim() && controlSetName.trim() !== controlSet?.controls_set_name) {
      onRename(controlSetName.trim());
    }
  };

  const isSubmitDisabled = !controlSetName.trim() || 
                          controlSetName.trim() === controlSet?.controls_set_name || 
                          isRenaming;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="md"
      hideCloseButton={true}
      classNames={{
        base: "max-w-md",
        wrapper: "z-[9999] p-2",
      }}
    >
      <ModalContent className="max-h-[500px]">
        <ModalBody className="p-6">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-zinc-900 text-center flex-grow">
                Rename Controls Set
              </h3>
              <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors" disabled={isRenaming}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="w-full flex flex-col items-start gap-1.5">
              <label htmlFor="controlSetName" className="text-sm font-normal text-[#18181B]">
                Name of Controls Set
              </label>
              <Input
                id="controlSetName"
                placeholder="Enter name of control set"
                value={controlSetName}
                onChange={(e) => {
                  setControlSetName(e.target.value);
                }}
                maxLength={45}
                className="w-full"
                classNames={{
                  inputWrapper: `bg-[#FAFAFA] border rounded-lg h-auto px-4 py-2 ${
                    errorMessage ? "border-red-500" : "border-[#E4E4E7]"
                  }`,
                  input: `text-sm placeholder:text-[#A1A1AA] ${
                    errorMessage ? "text-red-900" : ""
                  }`,
                }}
                variant="flat"
                isDisabled={isRenaming}
              />
              {errorMessage && (
                <p className="text-sm text-red-600 mt-1">
                  {errorMessage}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 pt-1 mt-auto">
              <div className="w-full border-t border-[#DCDCDC]" />
              <div className="flex justify-end gap-2 w-full pt-2">
                <ZButton
                  variant="bordered"
                  onClick={handleClose}
                  className="h-7 px-3 text-sm rounded-md border-[#006FEE] text-[#006FEE] font-medium"
                  isDisabled={isRenaming}
                >
                  Cancel
                </ZButton>
                <ZButton
                  color="primary"
                  onClick={handleSubmit}
                  isDisabled={isSubmitDisabled}
                  isLoading={isRenaming}
                  className="h-7 px-3 text-sm rounded-md bg-[#006FEE] text-white font-medium"
                >
                  Submit
                </ZButton>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RenameControlSetModal;